import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { tossLocalStorage, sessionStorage, storageKeys } from "@/utils/storage";

export const FOMO_DURATION_MS = 30 * 60 * 1000;

const ADS_DISCOUNT_CAMPAIGNS = [
  "naver_gfa_discount",
  "google_discount",
  "toss_may_tossform_260604_01_01",
  "toss_may_tossform_260604_01_02",
  "toss_may_tossform_260604_01_03",
  "toss_may_tossform_260604_01_04",
  "toss_may_tossform_260604_01_05",
  "toss_may_tossform_260604_01_06",
  "toss_m_banner_260609_01",
  "toss_m_banner_260609_02",
];

// 날짜 suffix가 반복 변경되는 캠페인 — prefix로 관리
const TM_CAMPAIGN_PREFIXES = [
  "tm_call_content_04_04_",
  "tm_call_content_02_04_",
];

// 캠페인명 포맷이 고정되지 않아 prefix 대신 포함 여부로 관리
const TM_CAMPAIGN_TOKENS = ["prdc10"];

// TM 계열: 게이팅 우회 + 타이머 없이 pending/reassurance 고정 (tm_call_content_* 와 동일 취급)
function isTmLikeCampaign(campaign: string): boolean {
  return (
    TM_CAMPAIGN_PREFIXES.some((prefix) => campaign.startsWith(prefix)) ||
    TM_CAMPAIGN_TOKENS.some((token) => campaign.includes(token))
  );
}

function matchesAdsDiscountCampaign(campaign: string): boolean {
  if (ADS_DISCOUNT_CAMPAIGNS.includes(campaign)) return true;
  return isTmLikeCampaign(campaign);
}

export function getVisitCount(): number {
  return Number(tossLocalStorage.get(storageKeys.DirectVisitCount) ?? 0);
}

function incrementVisitCount(): void {
  if (sessionStorage.get(storageKeys.FomoVisitCounted)) return;
  sessionStorage.set(storageKeys.FomoVisitCounted, "true");
  tossLocalStorage.set(
    storageKeys.DirectVisitCount,
    String(getVisitCount() + 1)
  );
}

export function checkQuizCouponActive(): boolean {
  if (typeof window === "undefined") return false;
  if (tossLocalStorage.get(storageKeys.IsRegistered) === "true") return false;
  return sessionStorage.get(storageKeys.QuizCouponActive) === "true";
}

export function checkFomoActive(): boolean {
  if (typeof window === "undefined") return false;

  // 광고 캠페인 유입 고객은 가입 여부·만료 여부 무관하게 FOMO 활성화
  if (sessionStorage.get(storageKeys.AdsDiscountCampaign) === "true")
    return true;

  if (tossLocalStorage.get(storageKeys.IsRegistered) === "true") return false;

  // CRM 직링크 재진입 고객은 FOMO 대상 아님
  if (sessionStorage.get(storageKeys.IsCrmReentry) === "true") return false;

  // productGroup이 있는데 "100"(정규 퍼널)이 아니면 프로모션 퍼널 → FOMO 차단
  const productGroup = sessionStorage.get(storageKeys.productGroup);
  if (productGroup && productGroup !== "100") return false;

  // 홈에서만 카운트 증가 — 아직 increment 실행 전이면 +1 미리 반영
  const isHome = window.location.pathname === "/";
  const effectiveVisitCount =
    getVisitCount() +
    (isHome && !sessionStorage.get(storageKeys.FomoVisitCounted) ? 1 : 0);

  if (effectiveVisitCount < 2) return false;

  const stored = tossLocalStorage.get(storageKeys.FomoStartAt);

  // 30분 만료 시 무조건 inactive
  if (stored && FOMO_DURATION_MS - (Date.now() - Number(stored)) <= 0)
    return false;

  // confirmation 도달(30분 이내) 또는 카운트다운 중 또는 pending(모달 미확인)
  return true;
}

export type FomoStatus = "inactive" | "pending" | "countdown" | "reassurance";

interface FomoState {
  status: FomoStatus;
  remainingMs: number;
  isModalShown: boolean;
  markConfirmationReached: () => void;
  markModalShown: () => void;
  startCountdown: () => void;
}

function getProductGroupFromUrl(url: string): string | null {
  const qs = url.includes("?") ? url.split("?")[1] : "";
  return new URLSearchParams(qs).get("productGroup");
}

export function useFomoState(): FomoState {
  const router = useRouter();
  const [status, setStatus] = useState<FomoStatus>("inactive");
  const [remainingMs, setRemainingMs] = useState(0);
  const [isModalShown, setIsModalShown] = useState(false);

  // pgFromUrl: routeChangeComplete 시 URL에서 직접 파싱한 값.
  // 홈(/)이면 null → productGroup 체크 스킵.
  // sessionStorage는 페이지 마운트 useEffect 이후에 갱신되므로 타이밍 불일치 발생 — URL 직접 사용.
  const computeAndSetStatus = useCallback((pgFromUrl?: string | null) => {
    const isAdsDiscountCampaign =
      sessionStorage.get(storageKeys.AdsDiscountCampaign) === "true";

    if (
      !isAdsDiscountCampaign &&
      tossLocalStorage.get(storageKeys.IsRegistered) === "true"
    ) {
      setStatus("inactive");
      return;
    }

    if (sessionStorage.get(storageKeys.IsCrmReentry) === "true") {
      setStatus("inactive");
      return;
    }

    const isHome = window.location.pathname === "/";
    const productGroup =
      pgFromUrl ?? sessionStorage.get(storageKeys.productGroup);
    if (!isHome && productGroup && productGroup !== "100") {
      setStatus("inactive");
      return;
    }

    const visitCount = getVisitCount();

    if (!isAdsDiscountCampaign && visitCount < 2) {
      setStatus("inactive");
      return;
    }

    setIsModalShown(
      tossLocalStorage.get(storageKeys.FomoModalShown) === "true"
    );

    // TM 캠페인은 타이머 없이 모달 확인 즉시 reassurance로 고정
    const isTmCampaign =
      sessionStorage.get(storageKeys.AdsDiscountCampaignIsTm) === "true";
    if (isTmCampaign) {
      const modalShown =
        tossLocalStorage.get(storageKeys.FomoModalShown) === "true";
      setStatus(modalShown ? "reassurance" : "pending");
      return;
    }

    const stored = tossLocalStorage.get(storageKeys.FomoStartAt);

    if (stored) {
      const remaining = FOMO_DURATION_MS - (Date.now() - Number(stored));
      if (remaining <= 0) {
        setStatus("inactive");
        return;
      }

      if (
        tossLocalStorage.get(storageKeys.FomoReachedConfirmation) === "true"
      ) {
        setStatus("reassurance");
        return;
      }

      setRemainingMs(remaining);
      setStatus("countdown");
      return;
    }

    setStatus("pending");
  }, []);

  // 마운트 시 초기화 — URL에서 직접 파싱하여 sessionStorage 타이밍 의존 제거
  // FomaBanner가 Layout에 있어 pages/index.tsx render body보다 먼저 실행되므로
  // AdsDiscountCampaign 감지도 여기서 직접 처리해야 타이밍이 보장됨
  useEffect(() => {
    if (window.location.pathname === "/") {
      incrementVisitCount();
    }

    const utmCampaign = new URLSearchParams(window.location.search).get(
      "utm_campaign"
    );
    if (utmCampaign && matchesAdsDiscountCampaign(utmCampaign)) {
      sessionStorage.set(storageKeys.AdsDiscountCampaign, "true");
      sessionStorage.set(
        storageKeys.AdsDiscountCampaignIsToss,
        String(utmCampaign.includes("toss"))
      );
      sessionStorage.set(
        storageKeys.AdsDiscountCampaignIsTm,
        String(isTmLikeCampaign(utmCampaign))
      );
      tossLocalStorage.remove(storageKeys.FomoStartAt);
      tossLocalStorage.remove(storageKeys.FomoReachedConfirmation);
      tossLocalStorage.remove(storageKeys.FomoModalShown);
    }

    const visitCount = getVisitCount();
    const isAdsDiscountCampaign =
      sessionStorage.get(storageKeys.AdsDiscountCampaign) === "true";

    if (!isAdsDiscountCampaign && visitCount < 2) return;

    const pgFromUrl = new URLSearchParams(window.location.search).get(
      "productGroup"
    );
    computeAndSetStatus(pgFromUrl);
  }, [computeAndSetStatus]);

  useEffect(() => {
    if (status !== "countdown") return;

    const interval = setInterval(() => {
      const stored = tossLocalStorage.get(storageKeys.FomoStartAt);
      if (!stored) return;

      const remaining = FOMO_DURATION_MS - (Date.now() - Number(stored));
      if (remaining <= 0) {
        setRemainingMs(0);
        setStatus("reassurance");
      } else {
        setRemainingMs(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // 라우트 변경 시마다 재계산 — URL에서 productGroup 직접 파싱 (sessionStorage 갱신 전에 실행되므로)
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const visitCount = getVisitCount();
      const isAdsDiscountCampaign =
        sessionStorage.get(storageKeys.AdsDiscountCampaign) === "true";

      if (!isAdsDiscountCampaign && visitCount < 2) return;

      const pgFromUrl = getProductGroupFromUrl(url);
      computeAndSetStatus(pgFromUrl);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events, computeAndSetStatus]);

  const markConfirmationReached = useCallback(() => {
    tossLocalStorage.set(storageKeys.FomoReachedConfirmation, "true");
    setStatus("reassurance");
  }, []);

  const markModalShown = useCallback(() => {
    tossLocalStorage.set(storageKeys.FomoModalShown, "true");
    setIsModalShown(true);
  }, []);

  const startCountdown = useCallback(() => {
    const isTmCampaign =
      sessionStorage.get(storageKeys.AdsDiscountCampaignIsTm) === "true";
    if (isTmCampaign) {
      setStatus("reassurance");
      return;
    }
    const startAt = Date.now();
    tossLocalStorage.set(storageKeys.FomoStartAt, String(startAt));
    setRemainingMs(FOMO_DURATION_MS);
    setStatus("countdown");
  }, []);

  return {
    status,
    remainingMs,
    markConfirmationReached,
    isModalShown,
    markModalShown,
    startCountdown,
  };
}
